import { FC } from "react";
import { useParams } from "react-router-dom";
import { Alert, AlertTitle, Paper } from "@mui/material";
import { useFetchEmployee } from "src/Pages/Company/Absences/api/employee.queries";
import { useFetchEmployeeAbsenceRequest } from "src/Pages/Company/Absences/api/absenceRequests.queries";
import { useGetAbsenceType } from "src/Pages/Company/Absences/api/absenceTypes.queries";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import Title from "src/Pages/Company/Absences/Title/Title";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import { Loader } from "src/Components/Loader/Loader";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import { StatusChip } from "src/Pages/Company/Absences/AbsenceRequest/StatusChip";
import AbsenceRequestForm from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestForm";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import EmployeeCardSkeleton from "src/Pages/Company/Absences/EmployeeCard/EmployeeCardSkeleton";
import EmployeeCard from "src/Pages/Company/Absences/EmployeeCard";
import {
  IAbsenceRequest,
  IAbsenceRequestFormAbsenceType,
  Status,
} from "./AbsenceRequest.models";

const normalizeAbsenceType = (
  absenceType: IAbsenceRequest["absenceType"]
): IAbsenceRequestFormAbsenceType => ({
  id: absenceType.absenceTypeId,
  name: absenceType.name,
  description: absenceType.description,
  presenceType: absenceType.presenceType,
  billingCycleType: absenceType.billingCycleType,
  approvers: absenceType.approvers,
  configurationOptions: absenceType.configurationOptions,
  customFieldsConfiguration: absenceType.customFieldsConfiguration,
  submitLimitationFrom: absenceType.configurationOptions.submitLimitationFrom,
  submitLimitationTo: absenceType.configurationOptions.submitLimitationTo,
});

const AbsenceRequestEdit: FC = () => {
  const { absenceRequestId, employeeId } = useParams<Record<string, string>>();
  const basePath = useBasePath();
  const logStatus = useLogStatus();

  if (!employeeId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No employee ID provided.",
      file: "AbsenceRequestEdit.tsx",
    });
  }

  if (!absenceRequestId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Request ID provided.",
      file: "AbsenceRequestEdit.tsx",
    });
  }

  const {
    data: employee,
    isFetching: isFetchingEmployee,
    isError: isErrorEmployee,
  } = useFetchEmployee(employeeId);

  const {
    data: absenceRequest,
    isFetching: isFetchingAbsenceRequest,
    isError: isErrorAbsenceRequest,
  } = useFetchEmployeeAbsenceRequest(absenceRequestId!);

  const {
    data: absenceType,
    isFetching: isFetchingAbsenceType,
    isError: isErrorAbsenceType,
  } = useGetAbsenceType(absenceRequest?.absenceType.absenceTypeId);

  if (!isFetchingEmployee && isFetchingAbsenceRequest) return <Loader />;

  if (isErrorEmployee || isErrorAbsenceRequest || isErrorAbsenceType)
    return <ErrorDataFetching />;

  if (!absenceRequest || !employee) return null;

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Kartoteki pracowników"),
            to: `${basePath}/absences/absence-records`,
          },
          {
            label: __("Lista wniosków"),
            to: `${basePath}/absences/employee/${employeeId}#requests`,
          },
          {
            label: __("Edycja wniosku"),
          },
        ]}
      />
      <Title
        title={`${__("Edycja wniosku")} ${absenceRequest.absenceType.name}`}
      />
      {isFetchingEmployee ? (
        <EmployeeCardSkeleton />
      ) : (
        <EmployeeCard data={employee} />
      )}
      <Paper
        sx={{ marginTop: "2rem", p: "2rem" }}
        data-testid="absence-request-edit"
      >
        {absenceRequest?.status === Status.CORRECTION_REQUIRED &&
          absenceRequest.note && (
            <Alert severity="warning" sx={{ mb: "1.5rem" }}>
              <AlertTitle>{__("Wymagane zmiany na wniosku")}:</AlertTitle>
              {absenceRequest.note.content}
            </Alert>
          )}
        <Fieldset legend={__("Status")}>
          <StatusChip variant={absenceRequest.status} />
        </Fieldset>
        {isFetchingAbsenceType && <Loader />}
        {absenceRequest && (
          <AbsenceRequestForm
            absenceType={normalizeAbsenceType(absenceRequest.absenceType)}
            employeeId={employeeId}
            employeeDailyWorkingHours={employee.dailyWorkingHours}
            absenceRequestData={absenceRequest}
          />
        )}
      </Paper>
    </>
  );
};

export default AbsenceRequestEdit;
